import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Authenticator, View, useTheme as useAmplifyTheme, useAuthenticator } from '@aws-amplify/ui-react'
import { Amplify } from 'aws-amplify'

import CenteredContainer from '../../components/CenteredContainer'
import './style.css'
import classRankedLogo from '../../assets/classRankedLogo.svg'
import classRankedLogoMob from '../../assets/classrankedLogo_Mob.svg'
import { Alert, Box, Link, InputLabel, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { env } from '../../env'
import useAsyncEffect from 'use-async-effect'
import { isEmpty } from 'lodash'
import { useCookies } from 'react-cookie'
import { fetchAuthSession, signOut, signInWithRedirect, getCurrentUser } from 'aws-amplify/auth'
import { grey } from '../../colors'
import { LoadingButton } from '@mui/lab'
import { useMutation } from '@tanstack/react-query'
import { type TenantInfo } from '../../models/TenantInfo'
import { configureAmplify } from '../../utils/cognito'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import axios from 'axios'
import LoadingPageV2 from '../LoadingPageV2'
import 'aws-amplify/auth/enable-oauth-listener'

const LOGIN_REDIRECT_URI = `${window.location.origin}/login`

const LoginPage: React.FC = (): JSX.Element => {
  const [searchParams, _] = useSearchParams()
  const [cookies, setCookie, removeCookie] = useCookies(['edlink_access_token_exists', 'edlink_refresh_token_exists'])
  const [email, setEmail] = useState('')
  const [showCognitoLogin, setShowCognitoLogin] = useState(false)
  const [manualLogin, setManualLogin] = useState(false)

  const state = searchParams.get('state')
  const code = searchParams.get('code')
  const error = searchParams.get('error')

  const navigate = useNavigate()
  const [apiError, setApiError] = useState(false)
  const [tokenLoading, setTokenLoading] = useState(false)

  const { route } = useAuthenticator((context) => [context.route])

  const { data: tenantData, mutate, mutateAsync, isPending, error: tenantError } = useMutation({
    gcTime: Infinity,
    mutationFn: async (username: string) => {
      const urlSearch = username?.split('@')?.[1]
      const res = await axios.get<TenantInfo>(`/api/tenant_info/${urlSearch}`)
      localStorage.setItem('tenantKey', urlSearch)

      configureAmplify(res.data)

      localStorage.setItem('tenantInfo', JSON.stringify(res.data))

      return res.data
    }
  })

  const { tokens } = useAmplifyTheme()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  function gotoEdlinkSSO (): void {
    setApiError(false)
    const state = window.crypto.randomUUID()
    localStorage.setItem('edlinkState', state)
    const EDLINK_SSO_URL = `https://ed.link/sso/login?client_id=${env.REACT_APP_EDLINK_CLIENT_ID}&redirect_uri=${encodeURI(LOGIN_REDIRECT_URI)}&response_type=code&state=${state}`
    window.location.href = EDLINK_SSO_URL
  }

  const edlinkLoggingIn = code !== undefined && code !== null

  useAsyncEffect(async () => {
    // if you are logging in with code; log out of any existing users
    if (!edlinkLoggingIn) {
      return
    }
    if (cookies.edlink_access_token_exists != null) {
      removeCookie('edlink_access_token_exists')
      removeCookie('edlink_refresh_token_exists')
      await axios.post('/api/auth/logout')
    }
    if (route === 'authenticated') {
      try {
        await signOut()
      } catch {}
    }
  }, [edlinkLoggingIn])

  useAsyncEffect(async () => {
    if (route === 'authenticated') {
      console.log('cognito authenticated')
      navigate('/', { replace: true, state: { fromLogin: true } })
    }
  }, [route])

  useAsyncEffect(async () => {
    try {
      const authData = await fetchAuthSession()
      if (authData?.tokens != null) {
        navigate('/', { replace: true, state: { fromLogin: true } })
      }
    } catch {}
  }, [])

  useAsyncEffect(async () => {
    if (cookies.edlink_access_token_exists != null) {
      console.log('edlink authenticated and navigating to main')
      navigate('/', { replace: true, state: { fromLogin: true } })
    }
  }, [cookies])

  useAsyncEffect(async () => {
    // this effect tries to call /api/token if we have code and state from oauth
    if (!edlinkLoggingIn || cookies.edlink_access_token_exists != null) {
      // dont run token fetch if we already have token, or dont have edlink info
      return
    }

    const edlinkState = localStorage.getItem('edlinkState')
    if (edlinkState && state !== edlinkState) {
      // check sso edlink state matches previous (for security)
      // localStorage.clear()
      setApiError(true)
      return
    }

    try {
      setTokenLoading(true)
      const result = await axios.post('/api/auth/token', {
        code,
        redirect_uri: LOGIN_REDIRECT_URI
      })

      if (!isEmpty(result?.data)) {
        localStorage.setItem('authType', 'edlink')
        console.log(result.data)
        const urlSearch = result?.data?.email?.split('@')?.[1]
        localStorage.setItem('tenantKey', urlSearch)
      }
    } catch {
      // localStorage.clear()
      setApiError(true)
    } finally {
      setTokenLoading(false)
    }
  }, [code, state])

  const formFields = {
    signIn: {
      username: {
        label: 'Institution Email',
        placeholder: 'Enter your Email'
      }
    },
    resetPassword: {
      username: {
        label: 'Enter your Institution Email',
        placeholder: 'Enter your Email'
      }
    }
  }

  useAsyncEffect(async () => {
    if (manualLogin) {
      localStorage.setItem('authType', 'cognito')
      setShowCognitoLogin(true)
      return
    }
    switch (tenantData?.default_login_method) {
      case 'idp':
        localStorage.setItem('authType', 'cognito')
        await signInWithRedirect({
          provider: { custom: 'CustomIDP' }
        })
        break
      case 'edlink':
        localStorage.setItem('authType', 'edlink')
        gotoEdlinkSSO()
        break
      case 'local':
        localStorage.setItem('authType', 'cognito')
        setManualLogin(false)
        setShowCognitoLogin(true)
        break
      default:
        break
    }
  }, [tenantData])

  if (tokenLoading || route === 'idle') {
    return <LoadingPageV2 />
  }

  if (showCognitoLogin) {
    return (
      <CenteredContainer>
        <View className='auth-wrapper'>
          <CustomHeader title={PAGE_TITLES.LOGIN} />
          <Authenticator
            formFields={formFields}
            hideSignUp={true}
            components={{
              Header: () => {
                // timeout ensures render of page before this code is run
                setTimeout(() => {
                  const usernameField = (document.getElementsByName('username')?.[0] as HTMLInputElement)
                  usernameField.defaultValue = email
                  usernameField.readOnly = true
                }, 0)
                return (
                  <View textAlign='center' padding={tokens.space.xl}>
                    <Box>
                      <img aria-label='ClassRanked logo' src={isMobile ? classRankedLogoMob : classRankedLogo} width={160} />
                    </Box>
                  </View>)
              },
              Footer: () => {
                return (
                  <View textAlign='center' padding={tokens.space.xl} marginTop={'-4rem'}>
                    <Button
                      type="button"
                      variation="link"
                      isFullWidth={false}
                      loadingText='Back'
                      onClick={() => { setManualLogin(false); setShowCognitoLogin(false) }}>
                      Back
                    </Button>
                  </View>)
              }
            }} />
        </View>
      </CenteredContainer>
    )
  }

  return (
  <CenteredContainer>
    <CustomHeader title={PAGE_TITLES.LOGIN} />
    <Stack textAlign='center'>
      <Box>
        <img aria-label='ClassRanked logo' src={isMobile ? classRankedLogoMob : classRankedLogo} width={160} />
      </Box>
      <Stack spacing={3}>
        <Typography variant='h1'>Log in to your account</Typography>
        <Typography variant='label'>Welcome! Please enter your details</Typography>
      </Stack>

      {!isEmpty(error) && <Alert severity='error'>Edlink SSO had an error</Alert>}

      {tenantError && <Alert severity='error'>User not found</Alert>}

      {apiError && <Alert severity='error'>Failed to authenticate using Edlink SSO</Alert>}

      <form onSubmit={(e) => {
        e.preventDefault()
        mutate(email)
      }}>
        <Stack textAlign='center'>
          <Box>
            <InputLabel sx={{ marginBottom: 2, textAlign: 'left', fontSize: '14px', color: grey[950] }}>
              Institution Email
            </InputLabel>
            <TextField required={true} value={email} onChange={(e) => { setEmail(e.target.value) }} placeholder="Institution Email" fullWidth/>
          </Box>

        <LoadingButton loading={isPending} size="large" variant="contained" type="submit">Continue</LoadingButton>
        </Stack>
      </form>

      <Link onClick={() => {
        localStorage.setItem('authType', 'cognito')
        setManualLogin(true)
        mutateAsync(email).then(() => {
          setShowCognitoLogin(true)
        }).catch(() => {})
      }} href='javascript:void(0)' sx={{ fontSize: '14px', textDecoration: 'none' }}>Try Manual Login</Link>
    </Stack>
  </CenteredContainer>
  )
}

export default LoginPage
